import { cn } from '@bem-react/classname'
import { OpUnitType } from 'dayjs'
import { AnimatePresence, motion } from 'framer-motion'
import { DetailedHTMLProps, FC, HTMLAttributes, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// import { DateFilterModel, FilterPeriodType } from '@/core/interfaces'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { useAppDispatch } from '@/core/store/hooks'

import { Memo } from '@/hoc/Memo'

import useWindowSize from '@/hooks/useWindowSize'

// import { getFilterTimeUTC } from '@/utils/getFilterTimeUTC'
// import { getDateWithoutTimeZone } from '@/utils/getTimePeriodFromDate'

// import { Icon } from '../Icon'
// import { DatePicker } from '../form/DatePicker'
import './Filter.scss'
import { getFilterTimeUTC } from '@/utils/getFilterTimeUTC'
import { getDateWithoutTimeZone } from '@/utils/getTimePeriodFromDate'
import {
  DateFilterModel,
  FilterPeriodType,
} from '@/core/interfaces/data-filter'
import { DatePicker } from '../DatePicker'
import { Icon } from '../Icon'
import { Select } from '../form/Select'

const cnFilter = cn('Filter')

const variants = {
  visible: {
    opacity: 1,
  },
  hidden: {
    opacity: 0,
  },
}

const mobile = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'tween',
      delay: 0.2,
    },
  },
  hidden: {
    opacity: 0,
    y: '100%',
    transition: {
      type: 'tween',
      delay: 0,
    },
  },
}

interface FilterListSchema {
  value: string
  label: OpUnitType
}

// interface FilterData {
//   filter: FilterPeriodType | string
//   startDate: string | Date
//   endDate: string | Date
// }

interface FilterSchema
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  isMobile?: boolean
  visible?: boolean
  isVisibleSelect?: boolean
  isVisibleRageDate?: boolean
  classNameSelect?: string
  filtersList?: FilterListSchema[]
  setVisible?: (b: boolean) => void
  changeDate?: ({ startDate, endDate }: DateFilterModel) => void
  changeFilter?: (filter: FilterPeriodType) => void
  defaultFilter?: FilterPeriodType | null
  isShowOnAllScreens?: boolean
}

export const Filter: FC<FilterSchema> = Memo(
  ({
    isMobile = false,
    visible = false,
    isVisibleSelect = true,
    isVisibleRageDate = true,
    className,
    classNameSelect,
    changeDate,
    setVisible,
    changeFilter,
    defaultFilter,
    filtersList,
  }: // isShowOnAllScreens = false,
  FilterSchema) => {
    const { t } = useTranslation('locales')
    const { lessTablet } = useWindowSize()
    const dispatch = useAppDispatch()

    const filters = [
      { value: 'day', label: t('Last year') },
      { value: 'week', label: t('Last 3 month') },
      { value: 'month', label: t('Last 6 month') },
    ]

    useEffect(() => {
      const { startDate, endDate } = defaultFilter
        ? getFilterTimeUTC(1, defaultFilter)
        : getFilterTimeUTC()

      reset({
        startDate: getDateWithoutTimeZone(startDate),
        endDate: getDateWithoutTimeZone(endDate),
        filter: defaultFilter ? defaultFilter : FilterPeriodType.week,
      })

      if (changeDate) {
        changeDate({
          startDate,
          endDate,
        })
      }
    }, [defaultFilter])

    const { control, reset, watch } = useForm({
      mode: 'onChange',
    })

    useEffect(() => {
      const subscription = watch((form, { type, name }) => {
        if (changeFilter && form.filter) {
          changeFilter(form.filter)
        }

        if (type === 'change' && name !== 'filter') {
          const { startDate, endDate } = getFilterTimeUTC(
            7,
            'day',
            new Date(form.startDate),
            new Date(form.endDate),
          )

          if (startDate <= endDate) {
            changeDate &&
              changeDate({
                startDate,
                endDate,
              })

            reset({
              startDate: form.startDate,
              endDate: form.endDate,
              filter: '',
            })
          } else {
            dispatch(
              setToast({
                type: ToastType.error,
                message: t(
                  `The start date must be equal or less than the current date`,
                ),
              }),
            )
          }

          return
        }

        if (type === 'change' && name === 'filter') {
          const val = form.filter

          const { startDate, endDate } = getFilterTimeUTC(1, val)

          changeDate &&
            changeDate({
              startDate,
              endDate,
            })

          reset({
            startDate: getDateWithoutTimeZone(startDate),
            endDate: getDateWithoutTimeZone(endDate),
            filter: val,
          })
        }
      })
      return () => subscription.unsubscribe()
    }, [watch, changeDate])

    const generateHtml = () => {
      return (
        <>
          {isVisibleSelect && (
            <Select
              className={cnFilter('select', [classNameSelect])}
              placeholder={t('Custom')}
              control={control}
              name='filter'
              options={filtersList ? filtersList : filters}
              optionLabel='label'
              optionValue='value'
              label={t('Time range') + ':'}
              selectType={'filter'}
              labelColor={'blue'}
              flexDirection={!lessTablet ? 'row' : 'column'}

              // isNone={isNone}
            />
          )}
          {isVisibleRageDate && (
            <>
              <DatePicker
                className={cnFilter('date')}
                name='startDate'
                placeholder='__/__/____'
                dateFormat='dd/MM/yyyy'
                label={t('From') + ':'}
                control={control}
                type={!isMobile ? 'filter' : undefined}
                labelColor={!isMobile ? 'blue' : 'dark'}
                fluid={isMobile}
                flexDirection={!lessTablet ? 'row' : 'column'}
              />
              <DatePicker
                className={cnFilter('date')}
                name='endDate'
                placeholder='__/__/____'
                dateFormat='dd/MM/yyyy'
                label={t('To') + ':'}
                control={control}
                type={!isMobile ? 'filter' : undefined}
                fluid={isMobile}
                flexDirection={!lessTablet ? 'row' : 'column'}
              />
            </>
          )}
        </>
      )
    }

    return (
      <>
        <AnimatePresence>
          {isMobile && visible && (
            <motion.div
              onClick={() => setVisible && setVisible(false)}
              className={cnFilter('overlay')}
              variants={variants}
              initial='hidden'
              exit='hidden'
              layout
              animate={visible ? 'visible' : 'hidden'}
            ></motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {visible && lessTablet && (
            <motion.div
              variants={mobile}
              initial='hidden'
              exit='hidden'
              layout
              animate={visible ? 'visible' : 'hidden'}
              className={cnFilter(
                { mobile: isMobile, open: isMobile && visible },
                [className],
              )}
            >
              {isMobile && (
                <button
                  type='button'
                  className={cnFilter('close')}
                  onClick={() => setVisible && setVisible(false)}
                >
                  <Icon name='close' width={15} height={15} fill='#4182E6' />
                </button>
              )}

              {generateHtml()}
            </motion.div>
          )}
        </AnimatePresence>

        {/* {(!lessTablet || isShowOnAllScreens) && ( */}
        <div
          className={cnFilter({ mobile: isMobile, open: isMobile && visible }, [
            className,
          ])}
        >
          {generateHtml()}
        </div>
        {/* // )} */}
      </>
    )
  },
)
